import type { IExternalFormMultiselectItem } from 'components/ExternalFormMultiselect';
import type { RatioTypeEnum } from 'gql/graphql';
import { Tooltip } from 'uikit';

import type React from 'react';
import { useRef } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { ApplicationAreaSelector } from './ApplicationAreaSelector';

interface IControlApplicationAreaSelectorProps {
    label?: string;
    appliesToUerIdsControl: UseControllerProps;
    ratioGroupIdControl: UseControllerProps;
    disabled?: boolean;
    coefficientType: RatioTypeEnum;
    disabledIds: number[];
    tooltipText?: string;
}

export const ControlApplicationAreaSelector: React.FC<IControlApplicationAreaSelectorProps> = (props) => {
    const { label, appliesToUerIdsControl, ratioGroupIdControl, disabled, coefficientType, disabledIds, tooltipText } =
        props;

    const { field: appliesToUerIdsField, fieldState: appliesToUerIdsFieldState } =
        useController(appliesToUerIdsControl);
    const { field: appliesToUerField } = useController({
        control: appliesToUerIdsControl.control,
        name: 'appliesToUerItems',
    });
    const { field: ratioGroupIdField, fieldState: ratioGroupIdFieldState } = useController(ratioGroupIdControl);
    const { field: ratioGroupField } = useController({
        control: ratioGroupIdControl.control,
        name: 'ratioGroup',
    });

    const onChange = (
        appliesToUerIds: number[],
        ratioGroupId: number | null,
        appliesToUerItems: IExternalFormMultiselectItem[],
        ratioGroup: null | IExternalFormMultiselectItem,
    ) => {
        appliesToUerIdsField.onChange(appliesToUerIds);
        ratioGroupIdField.onChange(ratioGroupId);

        appliesToUerField.onChange(appliesToUerItems.map((i) => ({ id: i.id, code: i.label, data: i.data })));
        ratioGroupField.onChange(
            ratioGroup ? { sectionId: ratioGroup.id, sectionName: ratioGroup.label, data: ratioGroup.data } : undefined,
        );
    };

    const ref = useRef(null);

    return (
        <div style={{ position: 'relative' }}>
            <ApplicationAreaSelector
                disabledIds={disabledIds}
                disabled={disabled}
                label={label}
                onChange={onChange}
                coefficientType={coefficientType}
                ratioGroup={ratioGroupField.value}
                appliesToUerItems={appliesToUerField.value || []}
                status={appliesToUerIdsFieldState.invalid || ratioGroupIdFieldState.invalid ? 'alert' : undefined}
                tooltipText={tooltipText}
            />
            <div ref={ref} style={{ position: 'absolute', right: 0, top: 0, height: '100%' }} />
            {appliesToUerIdsFieldState.invalid && appliesToUerIdsFieldState.error?.message && (
                <Tooltip size="s" direction="rightCenter" status="alert" anchorRef={ref}>
                    {appliesToUerIdsFieldState.error?.message}
                </Tooltip>
            )}
            {!appliesToUerIdsFieldState.invalid &&
                ratioGroupIdFieldState.invalid &&
                ratioGroupIdFieldState.error?.message && (
                    <Tooltip size="s" direction="rightCenter" status="alert" anchorRef={ref}>
                        {ratioGroupIdFieldState.error?.message}
                    </Tooltip>
                )}
        </div>
    );
};

ControlApplicationAreaSelector.displayName = 'ControlApplicationAreaSelector';
